import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';
import { LIST_NAME_PLACEHOLDER } from './NewListModal.js';
import { useToast } from '../ui/Toast.js';

/** The value the list picker uses for "a new list". */
const NEW_LIST = '';

export interface AddToListModalProps {
  /** In the order they should land on the list. */
  fundIds: readonly string[];
  /** What the dialog is asking, in words: "Add 3 funds to a list". */
  title: string;
  /** A name to offer for a new list, from what the grid is filtered to. */
  suggestedName?: string | undefined;
  /** Only a new list: saving a filtered grid is starting one. */
  newOnly?: boolean | undefined;
  onClose: () => void;
}

/**
 * Funds onto a list: an existing one, or a new one named here. Either way the
 * list opens afterwards, because the next thing to do with it is curate it.
 */
export function AddToListModal(props: AddToListModalProps): React.JSX.Element {
  const titleId = useId();
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const lists = useQuery(trpc.lists.list.queryOptions());
  // A list made since this query last ran leaves it invalidated but still
  // holding the old answer, which a remount serves while it refetches. The
  // picker shows only an answer that is not being replaced.
  const knownLists = lists.isFetching ? undefined : lists.data;
  const [target, setTarget] = useState(NEW_LIST);
  const [name, setName] = useState(props.suggestedName ?? '');
  const { onClose } = props;

  const done = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
    void queryClient.invalidateQueries({ queryKey: trpc.lists.get.queryKey({ id }) });
    onClose();
    navigate(`/lists/${id}`);
  };
  const create = useMutation(
    trpc.lists.create.mutationOptions({
      onSuccess: (answer) => done(answer.id),
      onError: (error) => toast(error.message),
    }),
  );
  const add = useMutation(
    trpc.lists.addItems.mutationOptions({
      onSuccess: (answer) => {
        if (answer.alreadyThere > 0) {
          toast(
            `${answer.alreadyThere} ${answer.alreadyThere === 1 ? 'was' : 'were'} already on it.`,
            { tone: 'info' },
          );
        }
        if (answer.list !== null) done(answer.list.id);
      },
      onError: (error) => toast(error.message),
    }),
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const isNew = props.newOnly === true || target === NEW_LIST;
  const canSave = isNew ? name.trim() !== '' : true;
  const pending = create.isPending || add.isPending;

  function save(): void {
    if (!canSave || pending) return;
    const fundIds = [...props.fundIds];
    if (isNew) create.mutate({ name: name.trim(), fundIds });
    else add.mutate({ id: target, fundIds });
  }

  return createPortal(
    <div
      className="modal-backdrop"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        className="modal add-to-list"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h2 id={titleId} className="modal-title">
          {props.title}
        </h2>
        {props.newOnly !== true && (
          <label className="field">
            <span className="subtle">List</span>
            <Select
              label="List"
              value={target}
              onChange={setTarget}
              placeholder="Loading the lists…"
              disabled={knownLists === undefined}
              options={
                knownLists === undefined
                  ? []
                  : [
                      ...knownLists.map((list) => ({
                        value: list.id,
                        label: `${list.name} (${list.itemCount})`,
                      })),
                      { value: NEW_LIST, label: 'A new list…' },
                    ]
              }
            />
          </label>
        )}
        {isNew && (
          <label className="field">
            <span className="subtle">Name</span>
            <input
              autoFocus
              value={name}
              placeholder={LIST_NAME_PLACEHOLDER}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        )}
        <div className="modal-actions">
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" type="submit" disabled={!canSave || pending}>
            {isNew ? 'Create list' : 'Add to list'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
